import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { KlaraProject } from '../../model/klara';
import { klaraApi } from '../../services/klaraApi';
import { useAppTranslation } from '../../services/i18n';
import { ModalProvider, useModal } from '../../services/modal';
import { PageTabs } from '../../components/PageTabs';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from './components/EmptyState';
import { StatusBadge } from './components/StatusBadge';

type Props = {
  onOpen: (id: string) => void;
  onCreate: () => Promise<void>;
};

/** A job the partner is still working on cannot be deleted. */
function isRunning(project: KlaraProject) {
  return !['completed', 'failed', 'cancelled', 'draft'].includes(project.status);
}

export function ProjectList({ onOpen, onCreate }: Props) {
  const t = useAppTranslation();
  const [mine, setMine] = useState(false);

  return (
    // Wraps the page so the tables below can reach the shared confirm dialog,
    // the same way Katalogy and Skupiny uživatelů mount it.
    <ModalProvider>
      {/* Fills the Angular host the same way as KlaraPage — see the note there. */}
      <Box className="layout-fill layout-column tw-absolute">
        <PageHeader
          title={t('klara.list.title')}
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onCreate()}
            >
              {t('klara.list.create')}
            </Button>
          }
        />

        {/* Same white panel as the project detail — see KlaraPage. */}
        <Box
        sx={{
          backgroundColor: 'var(--color-white)',
          flexGrow: 1,
          overflow: 'auto',
          // Pinned for the same reason as on the detail — see KlaraPage.
          '& > .MuiTabs-root': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'var(--color-white)',
          },
        }}
      >
        <PageTabs
          currentTab={mine ? 'mine' : 'all'}
          onTabChange={(tab) => setMine(tab === 'mine')}
          tabs={[
            {
              tab: 'all',
              label: t('klara.list.all'),
              content: () => (
                <ProjectTable mine={false} onOpen={onOpen} onCreate={onCreate} />
              ),
            },
            {
              tab: 'mine',
              label: t('klara.list.mine'),
              content: () => (
                <ProjectTable mine onOpen={onOpen} onCreate={onCreate} />
              ),
            },
          ]}
        />
        </Box>
      </Box>
    </ModalProvider>
  );
}

/**
 * The kebab the design puts at the end of a row.
 *
 * Its menu is **Detail** and **Archivovat**; archiving has no API yet (§3 of
 * the Figma review), so the second entry is the action we do have. Naming it
 * "Archivovat" would promise a place the project cannot go.
 */
function RowMenu({
  project,
  onOpen,
  onDelete,
}: {
  project: KlaraProject;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const t = useAppTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const close = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setAnchor(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('klara.list.rowActions')}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <MenuItem
          onClick={(e: React.MouseEvent<HTMLLIElement>) => {
            close(e);
            onOpen();
          }}
        >
          {t('klara.list.detail')}
        </MenuItem>
        <MenuItem
          disabled={isRunning(project)}
          onClick={(e: React.MouseEvent<HTMLLIElement>) => {
            close(e);
            onDelete();
          }}
        >
          {t(
            isRunning(project)
              ? 'klara.list.cannotDeleteRunning'
              : 'klara.list.delete'
          )}
        </MenuItem>
      </Menu>
    </>
  );
}

function ProjectTable({
  mine,
  onOpen,
  onCreate,
}: {
  mine: boolean;
  onOpen: (id: string) => void;
  onCreate: () => Promise<void>;
}) {
  const t = useAppTranslation();
  const modal = useModal();
  const [projects, setProjects] = useState<KlaraProject[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Blank panel until the first response lands, so the "no projects yet" pitch
  // does not flash on every tab switch. Deliberately the first load only: a
  // refetch must not blank the panel, or a search that matches nothing would
  // unmount the field being typed into and take the focus with it.
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    // Switching tabs reuses this component rather than remounting it — PageTabs
    // renders both tabs from the same position — so a stale delete or create
    // failure would otherwise follow the user to the other tab.
    setError(null);
    try {
      setProjects(await klaraApi.list({ mine, fulltext: search || undefined }));
    } finally {
      setLoaded(true);
    }
  }, [mine, search]);

  useEffect(() => {
    const handle = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [load, search]);

  // Deletion is a soft delete server-side, but there is no way back from the
  // UI, so the wording promises no recovery.
  const remove = async (project: KlaraProject) => {
    const confirmed = await modal.confirm({
      title: t('klara.list.deleteTitle'),
      message: t('klara.list.deleteMessage', {
        name: project.inputs.course_name || t('klara.list.untitled'),
      }),
      confirmText: t('klara.list.delete'),
      confirmColor: 'error',
    });
    if (confirmed !== 'CONFIRM') {
      return;
    }

    // Deliberately not the modal's `syncAction`: it awaits inside the rxjs
    // subscriber, so a rejected call never reaches `close()` and leaves the
    // dialog stuck behind its progress bar. Nothing else in the app uses it.
    // Which also means the failure has nowhere to surface but here.
    setError(null);
    try {
      await klaraApi.remove(project.id);
    } catch {
      // Our own copy: whatever the API says is a technical string meant for
      // support, and the row staying put is the whole of what the user needs.
      setError(t('klara.list.deleteFailed'));
      return;
    }
    await load();
  };

  // Wrapped rather than passed straight to onClick: bare, the handler would be
  // called with the click event, and a rejected create would leave the button
  // looking like it did nothing at all.
  const create = async () => {
    setError(null);
    try {
      await onCreate();
    } catch {
      setError(t('klara.list.createFailed'));
    }
  };

  const errorAlert = error && (
    <Alert severity="error" sx={{ margin: '16px 24px 0' }}>
      {error}
    </Alert>
  );

  if (!loaded) {
    return <Box sx={{ padding: '16px 24px', minHeight: '320px' }} />;
  }

  // The designed empty state replaces the whole panel, search field included.
  // A fruitless search is a different situation and the design does not cover
  // it: the field has to stay, or there is no way back to the full list.
  if (projects.length === 0 && !search) {
    return (
      <>
        {errorAlert}
        <EmptyState
          quote={t(mine ? 'klara.list.emptyMineQuote' : 'klara.list.emptyAllQuote')}
          body={t(mine ? 'klara.list.emptyMineBody' : 'klara.list.emptyAllBody')}
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={create}>
              {t('klara.list.create')}
            </Button>
          }
          steps={
            mine
              ? undefined
              : [1, 2, 3].map((step) => ({
                  title: t(`klara.list.emptyStep${step}Title`),
                  description: t(`klara.list.emptyStep${step}Body`),
                }))
          }
        />
      </>
    );
  }

  return (
    <>
    {errorAlert}
    <Box sx={{ padding: '0 24px 16px' }}>
      {/* Its own band above the table, as the frame has it. */}
      <Box sx={{ height: '80px', display: 'flex', alignItems: 'center' }}>
        <TextField
          variant="standard"
          label={t('klara.list.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '266px' }}
        />
      </Box>

      {projects.length === 0 && (
        <Typography sx={{ padding: '24px 0', color: 'var(--color-black-text)' }}>
          {t('klara.list.noSearchResults')}
        </Typography>
      )}

      {projects.length > 0 && (
      <Table size="small">
        {/* Fixed widths, as the frame sets them, so the columns do not shuffle
            with the content of the longest project name. */}
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '300px' }}>{t('klara.list.name')}</TableCell>
            <TableCell sx={{ width: '200px' }}>{t('klara.list.status')}</TableCell>
            <TableCell sx={{ width: '100px' }}>{t('klara.list.length')}</TableCell>
            <TableCell sx={{ width: '200px' }}>{t('klara.list.author')}</TableCell>
            <TableCell sx={{ width: '100px' }}>{t('klara.list.language')}</TableCell>
            <TableCell sx={{ width: '100px' }}>{t('klara.list.date')}</TableCell>
            <TableCell sx={{ width: '48px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map((project) => (
            <TableRow
              key={project.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onOpen(project.id)}
            >
              <TableCell>
                {project.inputs.course_name || t('klara.list.untitled')}
              </TableCell>
              <TableCell>
                <StatusBadge
                  status={project.status}
                  label={t(`klara.statuses.${project.status}`, {
                    defaultValue: project.status,
                  })}
                />
                {/* The sweep flagged this one as quiet at the partner. Ours,
                    with no counterpart in the design — but it sits next to the
                    status, so it wears the same pill. */}
                {project.stalled && (
                  <Box component="span" sx={{ marginLeft: '6px' }}>
                    <StatusBadge status="evaluation_ready" label={t('klara.list.stalled')} />
                  </Box>
                )}
              </TableCell>
              <TableCell>
                {project.inputs.course_length_words
                  ? t('klara.list.words', {
                      count: project.inputs.course_length_words,
                    })
                  : '—'}
              </TableCell>
              <TableCell>{project.author_name ?? '—'}</TableCell>
              <TableCell>
                {project.inputs.language
                  ? t(`klara.inputs.languages.${project.inputs.language}`, {
                      // An unfamiliar code still reads as itself rather than
                      // as a missing translation key.
                      defaultValue: project.inputs.language,
                    })
                  : '—'}
              </TableCell>
              <TableCell>
                {new Date(project.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell align="right">
                <RowMenu
                  project={project}
                  onOpen={() => onOpen(project.id)}
                  onDelete={() => remove(project)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
    </Box>
    </>
  );
}
